import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Map, GoogleApiWrapper } from 'google-maps-react';
import InfoWindowEx from './info-window-ex';
import { getSettings } from '../util/getSettings';
import { getResults } from '../util/getResults';
import { getMarkerIcon, getMarkerIconSolid } from '../util/getMarkerIcon';
import { manageNonMpcMarkers } from '../util/manageNonMpcMarkers';
import { mapBoundsUpdated } from '../redux/actions/map';
import { markerDataUpdated } from '../redux/actions/markerData';
import { groupResultsByLatLng } from '../util/groupResultsByLatLng';
import { getZoom } from '../util/zoom';
import MapTooltip from './map-tooltip';
const settings = getSettings(['century', 'settings']);

const markers = [];
const clusters = [];

const MapContainer = ({ google }) => {
  const [initLoadComplete, setInitLoadComplete] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [mapResized, setMapResized] = useState(false);
  const mapState = useSelector((state) => state.map);
  const communities = useSelector((state) => state.communities);
  const results = getResults(communities);
  const markerDataState = useSelector(state => state.markerData);
  const regionData = useSelector(state => state.regionData);
  const filters = useSelector((state) => state.filters);
  const dispatch = useDispatch();

  useEffect(() => {
    if (mapRef) {
      mapRef.setCenter(new google.maps.LatLng(regionData.initLat, regionData.initLng));
      mapRef.setZoom(getZoom(regionData.initZoom, regionData.initMobileZoom));
      dispatch(mapBoundsUpdated(mapRef.getBounds(), 'newRegion'));
    }
  }, [regionData.timeStamp]);

  useEffect(() => {
    const setMarkerByType = marker => {
      if (marker.type === 'standard') {
        marker.setIcon(getMarkerIcon(marker.company, markerDataState.activeMarker, marker.name));
      } else {
        marker.setIcon(getMarkerIconSolid(marker.company, markerDataState.activeMarker, marker.name));
      }
    };
    markers.forEach(setMarkerByType);
    clusters.forEach(cluster => {
      cluster.getMarkers().forEach(setMarkerByType);
    });
  }, [markerDataState.activeMarker]);

  useEffect(() =>{
    if (!mapState.visible) {
      dispatch(markerDataUpdated({
        showTooltip: false,
        activeMarker: null,
        activeResult: null
      }));
    }
  }, [mapState.visible]);

  useEffect(() =>{
    dispatch(markerDataUpdated({
      showTooltip: false,
      activeMarker: null,
      activeResult: null
    }));
  }, [filters.timeStamp]);

  const onMapClick = () => {
    dispatch(markerDataUpdated({
      ...markerDataState,
      showTooltip: false
    }))
  };

  const nonMpc = results;
  const latLngGroups = {};
  const mpcGroups = [];

  groupResultsByLatLng({ 
    groups: latLngGroups,
    results
  });

  const onMarkerClick = ({ result, groups }, marker) => {
    const mapWidth = document.querySelector('.map-container').offsetWidth
    const resultKey = `${result.latitude},${result.longitude}`;
    let newResult = result;
    let multipleOrSingle = 'single';

    if (mapWidth < 445) {
      document.documentElement.style.setProperty('--fyh-map-tooltip-width', '375px')
    } else {
      document.documentElement.style.setProperty('--fyh-map-tooltip-width', '445px')
    }

    if (latLngGroups && latLngGroups[resultKey]) {
      if (latLngGroups[resultKey].length > 1) {
        multipleOrSingle = 'multiple';
      }
      if (latLngGroups[resultKey].length > 1) {
        newResult = latLngGroups[resultKey][0];
      }
    }
    
    dispatch(markerDataUpdated({
      infoWindowType: multipleOrSingle,
      showTooltip: true,
      activeMarker: marker,
      activeResult: newResult
    }));
  };

  manageNonMpcMarkers({
    nonMpcResults: nonMpc,
    currentMarkers: markers,
    activeMarker: markerDataState.activeMarker,
    map: mapRef,
    onMarkerClick: onMarkerClick,
    latLngGroups
  });

  if (!mapResized) {
    let height = window.innerHeight;
    if (window.innerWidth > height) {
      height = window.innerWidth;
    }
    window.document.documentElement.style.setProperty('--mobile-map-height-adjust', (height - 135) + 'px');
    setMapResized(true);
  }
  
  return (
    <div 
      className={`map-container ${markerDataState.infoWindowType === 'multiple' && 'map-container-infowindow'} ${mapState.visible ? '' : 'd-none'}`}
      role="region"
      aria-label="map"
    >
      <Map
        google={google}
        initialCenter={{ lat: regionData.initLat, lng: regionData.initLng }}
        zoom={getZoom(regionData.initZoom, regionData.initMobileZoom)}
        zoomControl={true}
        zoomControlOptions={{ position: google.maps.ControlPosition.LEFT_TOP }}
        mapTypeControl={false}
        scaleControl={false}
        streetViewControl={false}
        panControl={false}
        rotateControl={false}
        fullscreenControl={false}
        onClick={onMapClick}
        onReady={(mapProps, map) => {
          map.setOptions({
            styles: settings.gmapsStyles
          });
          !mapRef && setMapRef(map);
        }}
        onTilesloaded={(mapProps, map) => {
          if (!initLoadComplete) {
            setInitLoadComplete(true);
            if (!communities.isFetching) {
              dispatch(mapBoundsUpdated(map.getBounds(), 'onTilesloaded'));
            }
          }
        }}
        onDragend={(mapProps, map) => {
          if (!communities.isFetching) {
            dispatch(mapBoundsUpdated(map.getBounds(), 'onDragend'));
          }
        }}
        onZoomChanged={(mapProps, map) => {
          if (!communities.isFetching) {
            dispatch(mapBoundsUpdated(map.getBounds(), 'onZoomChanged'));
          }
        }}
      >
        <InfoWindowEx marker={markerDataState.activeMarker}>
          <MapTooltip latLngGroups={latLngGroups} activeResult={markerDataState.activeResult} />
        </InfoWindowEx>
      </Map>
    </div>
  );
};

export default GoogleApiWrapper({
  apiKey: settings.gmapsApiKey
})(MapContainer);
